import React, { useState, useRef, KeyboardEvent, useEffect } from "react";
import { IVerificationCodeProps } from "containers/SignUp/types";
import Render from "components/Common/Render";

const VerificationCode: React.FC<IVerificationCodeProps> = ({
  phoneNumber,
  email,
  onVerify,
  isVerifying,
  verificationType: verificationTypeProp,
  onCodeChange,
  onSwitchVerificationType,
}) => {
  const [code, setCode] = useState(["", "", "", ""]);
  const hasAttemptedVerification = useRef(false); // Track if we've attempted verification
  const verifiedCodesRef = useRef(new Set<string>()); // Track which codes we've already verified
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  // Use verification type from props
  const verificationType =
    verificationTypeProp === "phone_number" ? "phone" : "email";

  // Auto-verify when code is complete (only on first attempt)
  useEffect(() => {
    const verificationCode = code.join("");

    // Only auto-verify if:
    // 1. Code is complete (4 digits)
    // 2. Not currently verifying
    // 3. Haven't already verified this exact code
    // 4. This is the first attempt (no previous verification tried)
    if (
      verificationCode.length === 4 &&
      !isVerifying &&
      !verifiedCodesRef.current.has(verificationCode) &&
      !hasAttemptedVerification.current
    ) {
      // Mark that we've attempted verification
      hasAttemptedVerification.current = true;

      // Mark this code as verified to prevent duplicates
      verifiedCodesRef.current.add(verificationCode);

      // Add a small delay for better UX
      const timer = setTimeout(() => {
        onVerify(verificationCode);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [code, isVerifying, onVerify]);

  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Notify parent of code change
    if (onCodeChange) {
      onCodeChange(newCode.join(""));
    }

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!code[index] && index > 0) {
        // If current box is empty, move to previous box
        inputRefs[index - 1].current?.focus();
      } else if (code[index]) {
        // If current box has value, clear it
        const newCode = [...code];
        newCode[index] = "";
        setCode(newCode);

        // Notify parent of code change
        if (onCodeChange) {
          onCodeChange(newCode.join(""));
        }
      }
    }
  };

  const handleVerify = () => {
    const verificationCode = code.join("");
    if (verificationCode.length === 4) {
      onVerify(verificationCode);
    }
  };

  const handleSwitchVerification = () => {
    // Reset code when switching
    setCode(["", "", "", ""]);

    // Reset verification attempt flag for fresh start
    hasAttemptedVerification.current = false;

    // Clear verified codes for fresh start
    verifiedCodesRef.current.clear();

    // Notify parent of code reset
    if (onCodeChange) {
      onCodeChange("");
    }

    // Notify parent to switch verification type
    if (onSwitchVerificationType) {
      onSwitchVerificationType();
    }

    // Focus first input
    inputRefs[0].current?.focus();
  };

  return (
    <div className="space-y-4 w-full">
      <div className="flex flex-col items-center space-y-4">
        {/* Verification Icon */}
        <div className="flex items-center justify-center">
          <svg
            width="70"
            height="69"
            viewBox="0 0 93 92"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.33337 79.1045V44.9848C4.50613 44.9848 4.59251 44.9848 4.76527 45.0712L34.7388 65.1975C39.0577 68.048 43.5494 68.048 47.9547 65.1975C57.8019 58.6327 67.7355 51.9815 77.7555 45.4167C77.9282 45.3303 78.1874 45.0712 78.5329 44.8984V78.068C78.5329 78.4135 78.5329 78.6726 78.4465 79.0181C77.9282 82.4733 74.905 84.9783 71.4498 84.8919H11.2437C8.91146 84.9783 6.75198 83.8553 5.54268 81.955C5.02441 81.0048 4.67889 80.0547 4.33337 79.1045Z"
              fill="#0102CE"
            />
            <path
              d="M4.33337 35.051C4.41975 34.7055 4.50613 34.2736 4.67889 34.0144C5.62906 31.3367 8.13405 29.4363 10.9846 29.35H71.2771C74.0412 29.1772 76.6326 30.732 77.7555 33.3234C79.2239 36.3467 78.2738 39.9746 75.4233 41.7021C72.0545 44.0344 68.5993 46.2802 65.1442 48.6125C58.6657 52.9314 52.1873 57.2504 45.7089 61.5693C42.5993 63.7288 40.0943 63.7288 36.9846 61.5693C27.6557 55.1773 18.154 48.7852 8.65232 42.6523C6.66561 41.2703 4.85165 39.8018 4.33337 37.2968V35.051Z"
              fill="#0102CE"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M82.9657 37.5953H30.3609C27.2513 37.5953 24.6599 35.0039 24.6599 31.8943V12.8045C24.5735 9.69488 27.0785 7.10352 30.1881 7.10352C30.2745 7.10352 30.2745 7.10352 30.3609 7.10352H82.9657C86.0753 7.10352 88.6667 9.69488 88.6667 12.8045V32.067C88.6667 35.1767 86.1617 37.6817 82.9657 37.5953Z"
              fill="#0102CE"
              stroke="white"
              strokeWidth="4"
              strokeMiterlimit="10"
            />
            <path
              d="M71.612 29.1304C70.8346 29.1304 70.2299 28.4394 70.1436 27.6619C70.1436 27.3164 70.2299 27.0573 70.4027 26.7982L72.3894 23.6021L68.2432 23.7749C67.4658 23.7749 66.7748 23.1702 66.7748 22.3065C66.7748 21.4427 67.3794 20.838 68.2432 20.838L72.303 21.0108L70.5755 18.1603C70.4027 17.8147 70.3163 17.4692 70.3163 17.1237C70.3163 16.3463 71.0074 15.7417 71.7848 15.6553C72.303 15.6553 72.7349 15.9144 72.9077 16.3463L74.8944 19.8015L76.622 16.6054C76.8811 16.0872 77.3994 15.828 77.9177 15.7417C78.6951 15.7417 79.2997 16.4327 79.3861 17.2101C79.3861 17.5556 79.2997 17.8147 79.127 18.0739L77.1403 21.2699L81.2864 21.0108C82.0639 21.0108 82.7549 21.6154 82.7549 22.4792C82.7549 23.343 82.1502 23.9477 81.2864 23.9477L77.1403 23.7749L79.0406 26.6254C79.2997 26.9709 79.3861 27.4028 79.2997 27.7483C79.2997 28.0938 79.127 28.5257 78.8678 28.7849C78.6087 29.044 78.3496 29.2168 78.004 29.2168C77.3994 29.2168 76.8811 28.8713 76.7084 28.353L74.808 25.0706L72.9077 28.2666C72.6486 28.7849 72.1303 29.1304 71.612 29.1304Z"
              fill="white"
              stroke="white"
            />
            <path
              d="M53.2997 28.9577C52.5223 28.9577 51.9177 28.3531 51.9177 27.5757C51.9177 27.3165 52.004 26.971 52.1768 26.7119L54.1635 23.6022L50.0173 23.775C49.2399 23.775 48.6353 23.084 48.6353 22.3065C48.6353 21.5291 49.2399 20.9245 50.0173 20.9245L53.9907 21.0972L52.2632 18.2467C52.0904 17.9012 52.004 17.5557 52.004 17.2102C52.004 16.4328 52.6087 15.8281 53.3861 15.8281C53.9044 15.8281 54.3363 16.0873 54.509 16.5192L56.4957 19.9743L58.2233 16.8647C58.4825 16.4328 59.0007 16.0873 59.519 16.0009C60.2964 16.0009 60.9011 16.6055 60.9011 17.3829C60.9011 17.6421 60.8147 17.9876 60.6419 18.2467L58.6552 21.3564L62.8014 21.1836C63.5788 21.1836 64.1835 21.8747 64.1835 22.6521C64.1835 23.4295 63.5788 24.0341 62.8014 24.0341L58.6552 23.8614L60.4692 26.7119C60.7283 27.0574 60.8147 27.4893 60.7283 27.8348C60.7283 28.1803 60.5555 28.6122 60.2964 28.8713C60.1237 29.1305 59.7781 29.3032 59.4326 29.3032C58.828 29.3032 58.3961 28.9577 58.1369 28.4395L56.323 25.1571L54.509 28.1803C54.4226 28.6986 53.9044 29.0441 53.2997 28.9577Z"
              fill="white"
              stroke="white"
            />
            <path
              d="M34.9011 28.9577C34.1237 28.9577 33.519 28.3531 33.519 27.5757C33.519 27.3165 33.6054 26.971 33.7782 26.7119L35.7649 23.6022L31.7051 23.775C30.9277 23.775 30.323 23.084 30.323 22.3065C30.323 21.5291 30.9277 20.9245 31.7051 20.9245L35.6785 21.0972L33.9509 18.2467C33.7782 17.9012 33.6918 17.5557 33.6918 17.2102C33.6918 16.4328 34.2964 15.8281 35.0738 15.8281C35.5921 15.8281 36.024 16.0873 36.1968 16.5192L38.1835 19.9743L39.9974 16.8647C40.2566 16.4328 40.7749 16.0873 41.2931 16.0009C42.0705 16.0009 42.6752 16.6055 42.6752 17.3829C42.6752 17.6421 42.5888 17.9876 42.4161 18.2467L40.4293 21.3564L44.5755 21.1836C45.3529 21.1836 45.9576 21.8747 45.9576 22.6521C45.9576 23.4295 45.3529 24.0341 44.5755 24.0341L40.4293 23.8614L42.2433 26.7119C42.5024 27.0574 42.5888 27.4893 42.5024 27.8348C42.5024 28.1803 42.3297 28.6122 42.0705 28.8713C41.8978 29.1305 41.5523 29.3032 41.2067 29.3032C40.6021 29.3032 40.1702 28.9577 39.9111 28.4395L38.0971 24.8979L36.2831 28.1803C36.024 28.6122 35.5057 28.9577 34.9011 28.9577Z"
              fill="white"
              stroke="white"
            />
            <path
              d="M37.1849 45.1103L35.5437 37.8545H43.4042L37.1849 45.1103Z"
              fill="#0102CE"
              stroke="white"
              strokeWidth="4"
              strokeMiterlimit="10"
            />
            <path
              d="M37.4441 42.6054L35.8029 35.3496H43.7497L37.4441 42.6054Z"
              fill="#0102CE"
            />
          </svg>
        </div>

        {/* Instruction Text - Changes based on verification type */}
        <div
          className="text-center font-medium"
          style={{
            maxWidth: "651px",
            fontFamily: "Poppins",
            fontWeight: 500,
            fontSize: "18px",
            lineHeight: "100%",
            letterSpacing: "0%",
            color: "#0B1215",
          }}
        >
          <Render
            when={verificationType === "email"}
            elseRender={
              <>
                Enter the verification code sent to your mobile phone{" "}
                <span style={{ fontWeight: 500 }}>{phoneNumber}</span>
              </>
            }
          >
            <>
              Enter the verification code sent to your email{" "}
              <span style={{ fontWeight: 500 }}>{email}</span>
            </>
          </Render>
        </div>

        {/* Code Input Boxes */}
        <div className="flex justify-center" style={{ gap: "40px" }}>
          {code.map((digit, index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              type="tel"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="text-center text-2xl font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{
                width: "60px",
                height: "60px",
                opacity: 0.6,
                borderRadius: "4px",
                padding: "12px",
                border: "1px solid #CCCCCC",
                fontSize: "14px",
                fontFamily: "Poppins",
              }}
            />
          ))}
        </div>

        {/* OR Divider */}
        <div className="text-center py-1">
          <p className="text-black font-bold text-base">OR</p>
        </div>

        {/* Switch Verification Method Link */}
        <span
          onClick={handleSwitchVerification}
          className="text-[#5856D6] font-semibold text-base cursor-pointer hover:underline"
        >
          {verificationType === "phone"
            ? "Verify by email address instead?"
            : "Verify by phone number instead?"}
        </span>

        {/* Agreement Text */}
        <p
          className="text-center w-full pt-6"
          style={{
            maxWidth: "1290px",
            fontFamily: "Poppins",
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "100%",
            letterSpacing: "0%",
            color: "#6A6A71",
          }}
        >
          By verifying your account, you agree to receive text messages from Open
          Rewards. Message and data rates may apply. Message frequency varies.
        </p>
      </div>
    </div>
  );
};

export default VerificationCode;
